/**
 * PersistedStorageMigration — zustand-dönemi kalıntılarının TEK SEFERLİK
 * localStorage migrasyonu (2026-07-18, zustand-temizliği).
 *
 * Tarihçe: store'lar eskiden zustand `persist` middleware'i ile
 * `{"state":{...},"version":n}` SARMALI formatta yazıyordu. mobx geçişinden
 * sonra MobXPersistence hidrasyonda bu sarmalı formatı da okuyabiliyordu
 * (geriye-uyum köprüsü). Köprü artık SÖKÜLDÜ — yeni format her zaman düz
 * `{prop: value}` JSON'dur. Mevcut kullanıcı verisinin kaybolmaması için bu
 * modül app-boot'ta (store singleton'ları kurulmadan ÖNCE, bkz. index.tsx
 * import sırası) çalışır ve:
 *
 *   1. Sarmalı-formatlı girdileri düz formata çevirip yeniden yazar.
 *   2. Eski anahtar adlarını kaynak-referans-hizalı yenilerine taşır
 *      ("tebhos-chat-drafts" → "Drafts"; yeni anahtar doluysa yenisi kazanır).
 *   3. Kendini "bir kez koştu" bayrağıyla (StorageMigration:v1) kapatır.
 *
 * Bayrak anahtarı BİLEREK "tebhos-" öneksiz: AccountStorage'ın hesap-başına
 * yönetilen-anahtar taşıması cihaz-seviyeli bu bayrağı hesapla birlikte
 * taşımamalı. Migrasyon idempotent'tir (düz girdiye unwrap no-op, kaynak
 * anahtar yoksa rename no-op) — bayrak silinse dahi veri bozulmaz.
 *
 * AccountStorage IndexedDB anlık-görüntüleri migrasyon-ÖNCESİ değerler
 * içerebilir (eski anahtar adı / sarmalı format); AccountStorage snapshot'ı
 * localStorage'a uygularken buradaki yardımcılarla normalize eder.
 */

import AppStorage from "@app/features/platform/state/PersistentStorage";

/** "Bir kez koştu" bayrağı (cihaz-seviyeli; hesapla taşınmaz). */
export const STORAGE_MIGRATION_FLAG_KEY = "StorageMigration:v1";

/**
 * Eski-ad → yeni-ad taşıma tablosu. kaynak-referansta localStorage-karşılığı olan
 * store'lar kaynak-referansın anahtar adını kullanır (envanter denetimi 2026-07-18:
 * kaynak-referans-eşli tek sapma taslak store'uydu). TEBHOS-özgü store'lar "tebhos-"
 * önekli kalır (AccountStorage yönetilen-önek sözleşmesi).
 */
export const LEGACY_STORE_KEY_RENAMES: Readonly<Record<string, string>> = {
  // kaynak-referans MessagingDrafts.ts → makePersistent(this, 'Drafts', ...).
  "tebhos-chat-drafts": "Drafts",
};

/**
 * Ham değer eski zustand-persist sarmalıysa (`{"state":{...},"version":n}` —
 * üst düzey anahtarlar {"state","version"} alt kümesi ve `state` düz nesne)
 * içteki `state`'in JSON'unu döndürür; değilse null (sarmal değil / bozuk).
 */
export function maybeUnwrapZustandEnvelope(raw: string | null): string | null {
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const topLevelKeys = Object.keys(record);
  const isZustandEnvelope =
    topLevelKeys.length > 0 &&
    topLevelKeys.every((key) => key === "state" || key === "version") &&
    typeof record.state === "object" &&
    record.state !== null &&
    !Array.isArray(record.state);
  if (!isZustandEnvelope) {
    return null;
  }
  try {
    return JSON.stringify(record.state);
  } catch {
    return null;
  }
}

/**
 * localStorage'daki tüm girdileri tarar: sarmalı formatı düz formata çevirir,
 * eski anahtar adlarını yenilerine taşır. İdempotent.
 */
export function migrateLegacyPersistedEntries(): void {
  for (const key of AppStorage.keys()) {
    const raw = AppStorage.getItem(key);
    if (raw === null) {
      continue;
    }
    const unwrapped = maybeUnwrapZustandEnvelope(raw);
    const targetKey = LEGACY_STORE_KEY_RENAMES[key];
    if (targetKey !== undefined) {
      // Yeni anahtar zaten doluysa (yeni kodla yazılmış) o kazanır; eski silinir.
      if (AppStorage.getItem(targetKey) === null) {
        AppStorage.setItem(targetKey, unwrapped ?? raw);
      }
      AppStorage.removeItem(key);
    } else if (unwrapped !== null) {
      AppStorage.setItem(key, unwrapped);
    }
  }
}

/**
 * SES CUTOVER KAPANIŞ TEMİZLİĞİ (2026-07-31) — emekliye ayrılan v1 ses
 * store'larının localStorage anahtarları. Her biri kod-tabanında grep ile
 * doğrulandı: yalnız `state/MediaEngineFacade.ts` (v1 motor, 0 canlı
 * importer) ve onun kendi `state/VoiceChannelConnector.ts`si tarafından
 * okunuyordu — v2 motor (`engine/MediaEngineFacade.ts`, canlı) hiçbirini
 * içe aktarmıyor. Kullanıcı elle temizlik yapmasın diye bu anahtarlar
 * tarayıcılarında bir kez silinir; veri kaybı riski yok (zaten okunmuyorlar).
 *
 *   - "tebhos-voice-local-state"      → LocalVoiceStateStore (bkz. dosya-başı
 *     notu: canlı v2 motor `state/LocalVoiceState.ts`yi kullanır, bu değil).
 *   - "tebhos-voice-participant-volume" → ParticipantVolumeStore (bkz. o
 *     dosyanın kendi "EMEKLİ" notu — canonical `state/ParticipantVolume.ts`
 *     ayrı anahtarla ("ParticipantVolume") kalıcı, silinmez).
 *
 * BİLİNÇLİ OLARAK burada silinmeyen: "tebhos-voice-settings" — tek-depo
 * birleşmesinde (2026-08-08) sahipli göçü VAR: `state/VoiceSettings.
 * applyLegacyTebhosStoreMigrationV1` değerleri tek depoya taşır,
 * `input/state/InputKeybind` PTT tercihini aldıktan sonra anahtarı SİLER.
 * Buradaki genel temizlik o sıralamaya karışmaz.
 */
export const RETIRED_VOICE_STORE_KEYS: ReadonlyArray<string> = [
  "tebhos-voice-local-state",
  "tebhos-voice-participant-volume",
];

/** Ayrı "bir kez koştu" bayrağı — `STORAGE_MIGRATION_FLAG_KEY` zaten set
 * olmuş mevcut kullanıcılarda da bu temizliğin çalışması için bağımsız. */
export const RETIRED_VOICE_STORE_CLEANUP_FLAG_KEY = "VoiceStoreCleanup:v1";

/** Emekli ses store anahtarlarını localStorage'dan siler. İdempotent. */
export function removeRetiredVoiceStoreKeys(): void {
  for (const key of RETIRED_VOICE_STORE_KEYS) {
    AppStorage.removeItem(key);
  }
}

/** Boot girişi: bayrak yoksa emekli ses-store temizliğini koşar. */
export function removeRetiredVoiceStoreKeysOnce(): void {
  if (AppStorage.getItem(RETIRED_VOICE_STORE_CLEANUP_FLAG_KEY) === "1") {
    return;
  }
  removeRetiredVoiceStoreKeys();
  AppStorage.setItem(RETIRED_VOICE_STORE_CLEANUP_FLAG_KEY, "1");
}

/** Boot girişi: bayrak yoksa migrasyonu koşar ve bayrağı yazar. */
export function runPersistedStorageMigrationOnce(): void {
  if (AppStorage.getItem(STORAGE_MIGRATION_FLAG_KEY) === "1") {
    return;
  }
  migrateLegacyPersistedEntries();
  AppStorage.setItem(STORAGE_MIGRATION_FLAG_KEY, "1");
}

// Modül import edilir edilmez koş — index.tsx bu modülü store içeren her
// modülden ÖNCE import eder (store singleton'ları import-anında hidre olur;
// migrasyon hidrasyondan önce bitmeli).
runPersistedStorageMigrationOnce();
removeRetiredVoiceStoreKeysOnce();
